import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContextZustand';
import { useSubscriptions } from '../../hooks/subscription';
import { SubscriptionStatusEnum, SubscriptionBillingTypeEnum } from '../../types/subscription';

const ALERT_DAYS = 5;

function daysUntil(dateStr: string): number {
  const due = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Banner de vencimento: exibe quando a assinatura ativa é PIX
 * e o próximo vencimento está a 5 dias ou menos. Não exibe para cartão (renovação automática).
 * Só chama a API de assinatura para admin (evita 403 para profissionais/atendentes).
 */
export const SubscriptionExpiryBanner: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.permissions?.includes('iam.manage') ?? false;
  const { latestSubscription, isLoading } = useSubscriptions({ enabled: isAdmin });

  if (!isAdmin) return null;

  const active =
    latestSubscription?.status === SubscriptionStatusEnum.ACTIVE ? latestSubscription : null;
  if (isLoading || !active?.nextDueDate) return null;
  if (active.billingType !== SubscriptionBillingTypeEnum.PIX) {
    return null;
  }

  const days = daysUntil(active.nextDueDate);
  if (days < 0 || days > ALERT_DAYS) return null;

  const message =
    days === 0
      ? 'Seu plano vence hoje. Pague a cobrança para manter o plano.'
      : days === 1
        ? 'Seu próximo vencimento é amanhã. Pague a cobrança para manter o plano.'
        : `Seu próximo vencimento é em ${days} dias. Pague a cobrança para manter o plano.`;

  return (
    <div className="bg-amber-600 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm font-medium">{message}</p>
          </div>
          <Link to="/subscription" className="text-sm font-semibold underline hover:no-underline">
            Ir para Assinatura
          </Link>
        </div>
      </div>
    </div>
  );
};
